/** Memory-only, mirrored draft shared by the compact and workspace cursor bars.
 *  One draft per account: there is one Denker, so switching the viewed project
 *  or picking a chat keeps what the user typed. */
import { useCallback, useSyncExternalStore, type SetStateAction } from "react";
import { create } from "zustand";
import {
  getDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import { mirroredStore } from "@/lib/mirrored-store";
import type { StandaloneBarChip } from "@/components/panels/standalone-bar-draft";
import type { InputBarRoutingIntent } from "@/stores/input-bar-target-store";

export interface StandaloneComposerDraft {
  text: string;
  chip: StandaloneBarChip | null;
  previousRoute: InputBarRoutingIntent | null;
  updatedAt: number;
}

export const EMPTY_DRAFT: StandaloneComposerDraft = { text: "", chip: null, previousRoute: null, updatedAt: 0 };

export interface StandaloneDraftMutation {
  scopeKey: string | null;
  draft: StandaloneComposerDraft;
}

/** The signed-in account's draft scope; null when signed out. */
export function getDraftScopeKey() {
  const scope = getDesktopChatAuthScope();
  return scope ? JSON.stringify([scope.accountId, scope.epoch]) : null;
}

interface StandaloneComposerDraftState {
  scopeKey: string | null;
  draft: StandaloneComposerDraft;
  lastMutation: StandaloneDraftMutation | null;
  updateDraft: (
    expectedScope: string | null,
    patch: Partial<StandaloneComposerDraft>,
    expectedText?: string,
  ) => void;
}

export const useStandaloneComposerDraftStore = create<StandaloneComposerDraftState>()(
  mirroredStore("standaloneComposerDraft", (set) => ({
    scopeKey: getDraftScopeKey(),
    draft: EMPTY_DRAFT,
    lastMutation: null,
    updateDraft: (expectedScope, patch, expectedText) => {
      if (getDraftScopeKey() !== expectedScope) return;
      set((state) => {
        const previous = state.scopeKey === expectedScope ? state.draft : EMPTY_DRAFT;
        if (expectedText !== undefined && previous.text !== expectedText) return state;
        if ((patch.text === undefined || patch.text === previous.text) &&
          (patch.chip === undefined || patch.chip === previous.chip) &&
          (patch.previousRoute === undefined || patch.previousRoute === previous.previousRoute) &&
          state.scopeKey === expectedScope) return state;
        const next = { ...previous, ...patch,
          updatedAt: Math.max(Date.now(), previous.updatedAt + 1) };
        return {
          scopeKey: expectedScope,
          draft: next,
          lastMutation: { scopeKey: expectedScope, draft: next },
        };
      });
    },
  })),
);

subscribeDesktopChatAuthScope(() => {
  useStandaloneComposerDraftStore.setState({ scopeKey: getDraftScopeKey(), draft: EMPTY_DRAFT, lastMutation: null });
});

export function useStandaloneComposerDraft() {
  const scopeKey = useSyncExternalStore(
    subscribeDesktopChatAuthScope,
    getDraftScopeKey,
    () => null,
  );
  const draft = useStandaloneComposerDraftStore((state) =>
    state.scopeKey === scopeKey ? state.draft : EMPTY_DRAFT,
  );
  const updateDraft = useCallback((patch: Partial<StandaloneComposerDraft>, expectedText?: string) => {
    useStandaloneComposerDraftStore.getState().updateDraft(scopeKey, patch, expectedText);
  }, [scopeKey]);
  const setValue = useCallback((next: SetStateAction<string>, expectedText?: string) => {
    const state = useStandaloneComposerDraftStore.getState();
    const current = state.scopeKey === scopeKey ? state.draft.text : "";
    updateDraft({ text: typeof next === "function" ? next(current) : next }, expectedText);
  }, [scopeKey, updateDraft]);
  return { draft, setValue, updateDraft };
}
